import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
  choosePayment,
  confirmSplitPaymentIdle,
  getDebtorInvoices,
  updateSignicatStatus,
  updateUboApiStatus,
} from '~/store/actions/invoiceActions';
import { ApiStatus, RootState } from '~/store/types/sharedTypes';
import SpinnerCircular from '~/components/shared/Spinner/SpinnerCircular';
import { MainContainer } from '~/styles/BaseStyledComponents';
import { theme } from '~/styles/themes';
import { InvoiceTabValue, OwnerApiInterface } from '~/Interfaces';
import { formatAmount } from '~/helpers/formatters.helper';
import { InvoiceLoaderHolder } from '../../Invoice';
import SomethingWentWrong from '../SomethingWentWrong';
import PaymentConfirmation from './PaymentConfirmation';
import Ubo from './UBO/Ubo';
import BankID from './BankID';
import InstalmentCards from './Instalments/InstalmentCards';

enum PayInInstalmentsViewEnum {
  Instalments,
  VerifyOwnership,
  SignWithBankID,
  ConfirmInBankIdApp,
  Loading,
  Success,
  Error,
}

interface Props {
  onCancelButton: () => void;
}

const PayInInstalments: React.FC<Props> = (props) => {
  const { onCancelButton } = props;
  const [currentView, setCurrentView] = useState<PayInInstalmentsViewEnum>(
    PayInInstalmentsViewEnum.Instalments
  );
  const [personalNumber, setPersonalNumber] = useState<string>('');
  const [beneficialOwners, setBeneficialOwners] = useState<OwnerApiInterface[]>(
    []
  );

  const history = useHistory();

  const {
    confirmTermsAndConditionsApiStatus,
    choosePaymentApiStatus,
    uboApiStatus,
    signicatApiStatus,
  } = useSelector((state: RootState) => state.factoring);

  const {
    partPaymentPlan,
    debtor,
    externalId,
    id,
    debtorOrgNumber,
  } = useSelector((state: RootState) => state.factoring.singleInvoice);

  useEffect(() => {
    if (
      confirmTermsAndConditionsApiStatus === ApiStatus.Started ||
      uboApiStatus === ApiStatus.Started
    ) {
      setCurrentView(PayInInstalmentsViewEnum.Loading);
    } else if (
      confirmTermsAndConditionsApiStatus === ApiStatus.Failed ||
      uboApiStatus === ApiStatus.Failed ||
      signicatApiStatus === ApiStatus.Failed
    ) {
      setCurrentView(PayInInstalmentsViewEnum.Error);
    } else if (
      uboApiStatus === ApiStatus.Completed &&
      confirmTermsAndConditionsApiStatus !== ApiStatus.Completed
    ) {
      setCurrentView(PayInInstalmentsViewEnum.SignWithBankID);
    } else if (
      confirmTermsAndConditionsApiStatus === ApiStatus.Completed &&
      choosePaymentApiStatus === ApiStatus.Completed &&
      signicatApiStatus !== ApiStatus.Completed
    ) {
      setCurrentView(PayInInstalmentsViewEnum.ConfirmInBankIdApp);
    } else if (signicatApiStatus === ApiStatus.Completed) {
      setCurrentView(PayInInstalmentsViewEnum.Success);
    }

    if (
      confirmTermsAndConditionsApiStatus === ApiStatus.Failed ||
      choosePaymentApiStatus === ApiStatus.Failed ||
      signicatApiStatus === ApiStatus.Failed ||
      uboApiStatus === ApiStatus.Failed
    ) {
      setCurrentView(PayInInstalmentsViewEnum.Error);
    }
  }, [
    confirmTermsAndConditionsApiStatus,
    uboApiStatus,
    signicatApiStatus,
    choosePaymentApiStatus,
  ]);

  const dispatch = useDispatch();

  const onDone = () => {
    dispatch(
      getDebtorInvoices(
        debtorOrgNumber ?? debtor.identificationNumber,
        1,
        10,
        InvoiceTabValue.OPEN
      )
    );
    history.push({
      pathname: '/invoices',
    });
  };

  const uboCanceled = () => {
    setCurrentView(PayInInstalmentsViewEnum.Instalments);
  };

  const onInstalmentsConfirm = (personalNumberInput: string) => {
    setPersonalNumber(personalNumberInput);
    if (debtor.uboApproved) {
      setCurrentView(PayInInstalmentsViewEnum.SignWithBankID);
      dispatch(updateUboApiStatus(ApiStatus.Completed));
    } else {
      setCurrentView(PayInInstalmentsViewEnum.VerifyOwnership);
    }
  };

  const resetApiStatuses = () => {
    dispatch(choosePayment(ApiStatus.Idle));
    dispatch(updateUboApiStatus(ApiStatus.Idle));
    dispatch(updateSignicatStatus(ApiStatus.Idle));
    dispatch(confirmSplitPaymentIdle());
  };

  const onTryAgain = () => {
    resetApiStatuses();
    onCancelButton();
  };

  const confirmBeneficialOwners = (owners: OwnerApiInterface[]) => {
    if (owners) {
      setBeneficialOwners(owners);
    }
  };

  const partPaymentCancel = () => {
    onCancelButton();
  };

  const signWithPersonalNumberCanceled = () => {
    if (debtor.uboApproved) {
      setCurrentView(PayInInstalmentsViewEnum.Instalments);
    } else {
      setCurrentView(PayInInstalmentsViewEnum.VerifyOwnership);
      dispatch(updateUboApiStatus(ApiStatus.Idle));
    }
  };

  switch (currentView) {
    case PayInInstalmentsViewEnum.Instalments:
      return (
        <InstalmentCards
          onCancelButton={partPaymentCancel}
          onConfirm={onInstalmentsConfirm}
          partPaymentPlan={partPaymentPlan}
          invoiceId={externalId ?? id}
        />
      );
    case PayInInstalmentsViewEnum.VerifyOwnership:
      return (
        <Ubo
          onCancelButton={uboCanceled}
          onAddOwners={confirmBeneficialOwners}
        />
      );
    case PayInInstalmentsViewEnum.SignWithBankID:
      return (
        <InstalmentCards
          withBankId
          onCancelButton={signWithPersonalNumberCanceled}
          onConfirm={onInstalmentsConfirm}
          partPaymentPlan={partPaymentPlan}
          invoiceId={externalId ?? id}
        />
      );
    case PayInInstalmentsViewEnum.ConfirmInBankIdApp:
      return <BankID personalNumber={personalNumber} />;
    case PayInInstalmentsViewEnum.Success:
      return (
        <PaymentConfirmation
          onDone={onDone}
          eachPayment={formatAmount(+partPaymentPlan[0].annuityAmount)}
          date={partPaymentPlan[0].plannedDate}
        />
      );
    case PayInInstalmentsViewEnum.Loading:
      return (
        <MainContainer>
          <InvoiceLoaderHolder>
            <SpinnerCircular color={theme.colors.secondary} />
          </InvoiceLoaderHolder>
        </MainContainer>
      );
    default:
      return <SomethingWentWrong onTryAgain={onTryAgain} />;
  }
};

export default PayInInstalments;
